import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import { 
  Code, Brain, BarChart, FileSearch, Award, ClipboardList, 
  Lightbulb, GitBranch, Blocks, BookOpen, Users, Workflow,
  Shield, Building2, Plane, Clock, Phone, ShoppingBag, 
  Trophy, Calculator, Search, Target, LineChart, RefreshCw, 
  CheckCircle2, PenTool as Tool, ArrowRight, Sparkles,
  Laptop, Presentation, Puzzle, Zap, Eye, MessageSquare,
  TrendingUp, Star, Settings, Database, Monitor
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import PageTransition from '../components/PageTransition';
import GradientHero from "../components/GradientHero";
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Expertise = () => {

  const [showApproach, setShowApproach] = useState(false);

  const expertiseJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Testing & QA Services",
    "description": "Services complets de Testing et QA : stratégie, automatisation, performance",
    "provider": {
      "@type": "Organization",
      "name": "Neoqualis"
    },
    "serviceType": "Quality Assurance Services",
    "areaServed": "FR"
  };

  const qaFunctional = {
    title: 'QA Fonctionnel',
    whatWeDo: [
      'Stratégie de test, plans & cas traçables aux exigences (Xray / Squash TM)',
      'Exécution web, mobile et API ; gestion et priorisation des anomalies',
      'Recette, UAT, accompagnement mise en production'
    ],
    deliverables: [
      'Plan de test',
      'Cahier de tests',
      'PV de recette',
      'Matrice de traçabilité',
      'Rapport de campagne'
    ],
    kpis: [
      'Couverture',
      'Taux de réussite',
      'Défauts par sprint',
      'Sévérité',
      'MTTR'
    ]
  };

  const qaAutomation = {
    title: 'QA Automatisation',
    whatWeDo: [
      'Cadrage ROI & architecture (Cypress, Selenium, Robot Framework)',
      'Frameworks maintenables (POM/Screenplay, data-driven, BDD Gherkin)',
      'Intégration CI/CD (GitLab, Jenkins, Azure DevOps), déflakiness & observabilité',
      'Performance : plans de charge JMeter, analyse des goulots & recommandations'
    ],
    deliverables: [
      'Référentiel de tests automatisés',
      'Rapports CI',
      'Indicateurs de stabilité',
      'Scripts JMeter'
    ],
    qualisAI: 'Génération assistée de scénarios Gherkin depuis les specs, export Xray/Squash, passerelles vers l\'automatisation'
  };

  const qaLead = {
    title: 'QA Lead / Test Management',
    whatWeDo: [
      'Définition de la stratégie, gouvernance, risk-based testing',
      'Pilotage d\'équipe, staffing, budget & planning',
      'Reporting hebdo, tableaux de bord, comités projets'
    ],
    deliverables: [
      'Stratégie de test',
      'Plan qualité',
      'RACI',
      'Dashboards KPI/OKR',
      'Compte-rendus & plans d\'action'
    ]
  };

  const auditConseil = {
    title: 'Audit & Conseil',
    whatWeDo: [
      'Diagnostic de maturité (process, outils, orga, perfo)',
      'Quick wins 10 jours et roadmap 90 jours',
      'Playbook & standards (templates, checklists, DoR/DoD)'
    ],
    deliverables: [
      'Rapport d\'audit',
      'Score par domaine',
      'Plan d\'actions priorisé',
      'Modèles prêts à l\'emploi'
    ]
  };

  const businessAnalysis = [
    {
      title: 'Analyse des besoins',
      description: 'Ateliers de recueil, clarification du périmètre, user stories & critères d\'acceptation, règles de gestion et cas limites.',
      icon: Lightbulb
    },
    {
      title: 'Conception solution',
      description: 'Story-mapping, prototypage, alignement avec les équipes IT, gestion des dépendances.',
      icon: Puzzle
    },
    {
      title: 'Gestion du changement',
      description: 'Plan de communication, formation utilisateurs, support au déploiement, suivi d\'adoption.',
      icon: Users
    }
  ];

  const projectManagement = [
    {
      title: 'Méthodes Agiles',
      description: 'Scrum / Kanban, sprints courts, cérémonies, amélioration continue.',
      icon: GitBranch
    },
    {
      title: 'Cycle en V',
      description: 'Phases cadrées, jalons de validation, gestion documentaire & conformité.',
      icon: Workflow
    },
    {
      title: 'PMO',
      description: 'Pilotage transverse, gestion des risques, planning & budget, reporting exécutif.',
      icon: Presentation
    }
  ];

  const otherServices = [
    {
      title: 'Formation (Qualis-Academy)',
      description: 'Parcours ISTQB, automatisation, Agile Testing, outillage (Xray/Squash, Cypress/Selenium).',
      deliverables: 'Supports, TP, certification, plan de progression.',
      icon: BookOpen
    },
    {
      title: 'Support & Maintenance QA',
      description: "Run des tests, maintien des suites d'automatisation, évolution des jeux de données, documentation. Nous assurons aussi le support utilisateur N2, la conduite du changement et le maintien en conditions opérationnelles pour garantir la continuité et l’efficacité des projets.",
      icon: Tool
    },
    {
      title: 'Innovation QA',
      description: 'Veille outils/pratiques, IA dans le testing (QualisAI), QA pour mobile/IoT, DevOps & observabilité.',
      icon: Sparkles
    }
  ];

  const domains = [
    {
      title: 'Banque & Assurance',
      description: 'Parcours clients, KYC/paiement, conformité & sécurité.',
      icon: Building2
    },
    {
      title: 'Retraite & protection sociale',
      description: 'Calculs/référentiels, qualité des données, portails.',
      icon: Clock
    },
    {
      title: 'Télécoms & industrie',
      description: 'Performance/scalabilité, selfcare, disponibilité.',
      icon: Phone
    },
    {
      title: 'Hôtellerie & services',
      description: 'Expérience omnicanale, réservation, fidélité.',
      icon: Trophy
    },
    {
      title: 'E-commerce',
      description: 'Tunnels de vente, fiabilité & performance, optimisation UX.',
      icon: ShoppingBag
    }
  ];

  const methodology = [
      {
      step: 1,
      title: 'Analyse & cadrage',
      description: 'Comprendre les enjeux business, contraintes et risques. Identifier les fonctionnalités critiques.',
      deliverables: 'Cadrage projet, RACI, backlog initial, matrice risques.',
      icon: Search
    },
    {
      step: 2,
      title: 'Stratégie QA',
      description: 'Définir les niveaux de tests, les types (fonctionnel, auto, perf), le scope et les critères de réussite.',
      deliverables: 'Stratégie de test, plan de couverture, priorisation des fonctionnalités critiques.',
      icon: Target
    },
    {
      step: 3,
      title: 'Préparation & environnements',
      description: 'Mise en place des environnements (dev, QA, préprod), données de test, jeux de données anonymisés, outillage.',
      deliverables: 'Données de test validées, environnements disponibles, CI/CD intégrée.',
      icon: Database
    },
    {
      step: 4,
      title: 'Conception & design des tests',
      description: 'Écriture des cas de test, scénarios Gherkin, automatisation des parcours critiques.',
      deliverables: 'Référentiel de tests (Xray/Squash), scénarios Gherkin, scripts automatisés.',
      icon: ClipboardList
    },
    {
      step: 5,
      title: 'Exécution & reporting',
      description: 'Lancer les campagnes, suivre les anomalies, produire un reporting clair pour décider du Go/No-Go.',
      deliverables: 'PV de recette, rapport de campagne, dashboards QA.',
      icon: LineChart
    },
    {
      step: 6,
      title: 'Amélioration continue',
      description: 'Capitaliser, réduire le flakiness, améliorer la couverture et intégrer les retours métiers.',
      deliverables: 'Playbook QA enrichi, leçons apprises, plan d’amélioration.',
      icon: RefreshCw
    }
  ];

  const tools = [
    {
      category: 'Test Management',
      tools: ['Jira', 'Xray', 'Squash TM', 'TestRail', 'HP ALM', 'Zephyr']
    },
    {
      category: 'Automatisation',
      tools: ['Cypress', 'Selenium', 'Playwright', 'Robot Framework', 'Postman', 'ReadyAPI', 'Katalon Studio']
    },
    {
      category: 'Performance & Charge',
      tools: ['JMeter', 'Gatling', 'Locust', 'k6']
    },
    {
      category: 'CI/CD & DevOps',
      tools: ['GitLab CI', 'Jenkins', 'Azure DevOps', 'GitHub Actions', 'CircleCI']
    },
    {
      category: 'Quality Monitoring',
      tools: ['SonarQube', 'Allure Report', 'ReportPortal', 'Grafana']
    },
    {
      category: 'Test Data & Environnements',
      tools: ['Mockoon', 'WireMock', 'Faker.js', 'DbUnit']
    },
    {
      category: 'Collaboration & Knowledge',
      tools: ['Confluence', 'Teams', 'Slack', 'Miro', 'Klaxoon', 'Notion']
    },
    {
      category: 'Agilité & Gestion produit',
      tools: ['Jira Align', 'Targetprocess', 'Trello', 'Azure Boards']
    }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5
      }
    })
  };

  return (
    <PageTransition>
      <SEOHead 
        title="Nos expertises — Testing, QA, Business Analysis & Automatisation | Neoqualis"
        description="Découvrez nos expertises en Testing & QA, Business Analysis, automatisation CI/CD, performance JMeter et formations Qualis-Academy. Solutions complètes pour sécuriser vos projets."
        canonical="https://neoqualis.com/expertise"
        jsonLd={expertiseJsonLd}
      />
      <div className="pt-16">
        {/* Hero Section */}
        <GradientHero
          title="Nos expertises"
          subtitle="Une approche complète de la qualité logicielle et du lien produit–métier, 
                adaptée à vos enjeux."
        />

        {/* Testing & QA */}
        <section id="qa" className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Testing & QA</h2>
              <p className="subheading max-w-2xl mx-auto">
                Une expertise de bout en bout : de la stratégie aux résultats mesurés.
              </p>
            </AnimatedSection>

            {/* QA Fonctionnel */}
            <div className="mb-16">
              <AnimatedSection>
                <div className="card max-w-6xl mx-auto">
                  <div className="flex items-center mb-6">
                    <div className="p-3 rounded-full bg-primary bg-opacity-10">
                      <FileSearch className="text-primary w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-semibold ml-4 text-gray-900">{qaFunctional.title}</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div>
                      <h4 className="text-lg font-semibold mb-4 text-gray-900">Ce que nous faisons</h4>
                      <ul className="space-y-2">
                        {qaFunctional.whatWeDo.map((item, i) => (
                          <li key={i} className="flex items-start text-gray-700">
                            <CheckCircle2 className="w-5 h-5 text-secondary mr-2 mt-0.5" />
                            <span className="text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-semibold mb-4 text-gray-900">Livrables</h4>
                      <div className="flex flex-wrap gap-2">
                        {qaFunctional.deliverables.map((item, i) => (
                          <span key={i} className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-semibold mb-4 text-gray-900">KPI</h4>
                      <div className="flex flex-wrap gap-2">
                        {qaFunctional.kpis.map((item, i) => (
                          <span key={i} className="text-sm bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-full">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* QA Automatisation */}
            <div className="mb-16">
              <AnimatedSection>
                <div className="card max-w-6xl mx-auto">
                  <div className="flex items-center mb-6">
                    <div className="p-3 rounded-full bg-secondary bg-opacity-10">
                      <Code className="text-secondary w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-semibold ml-4 text-gray-900">{qaAutomation.title}</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                    <div>
                      <h4 className="text-lg font-semibold mb-4 text-gray-900">Ce que nous faisons</h4>
                      <ul className="space-y-2">
                        {qaAutomation.whatWeDo.map((item, i) => (
                          <li key={i} className="flex items-start text-gray-700">
                            <CheckCircle2 className="w-5 h-5 text-secondary mr-2 mt-0.5" />
                            <span className="text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-semibold mb-4 text-gray-900">Livrables</h4>
                      <div className="flex flex-wrap gap-2">
                        {qaAutomation.deliverables.map((item, i) => (
                          <span key={i} className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="card bg-orange-50 border border-orange-200">
                    <div className="flex items-center mb-3">
                      <Sparkles className="w-6 h-6 text-orange-600 mr-3" />
                      <h4 className="text-lg font-semibold text-gray-900">+ QualisAI</h4>
                    </div>
                    <p className="text-gray-700">{qaAutomation.qualisAI}</p>
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* QA Lead / Test Management */}
            <div className="mb-16">
              <AnimatedSection>
                <div className="card max-w-6xl mx-auto">
                  <div className="flex items-center mb-6">
                    <div className="p-3 rounded-full bg-green-100">
                      <BarChart className="text-green-600 w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-semibold ml-4 text-gray-900">{qaLead.title}</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-lg font-semibold mb-4 text-gray-900">Ce que nous faisons</h4>
                      <ul className="space-y-2">
                        {qaLead.whatWeDo.map((item, i) => (
                          <li key={i} className="flex items-start text-gray-700">
                            <CheckCircle2 className="w-5 h-5 text-secondary mr-2 mt-0.5" />
                            <span className="text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-semibold mb-4 text-gray-900">Livrables</h4>
                      <div className="flex flex-wrap gap-2">
                        {qaLead.deliverables.map((item, i) => (
                          <span key={i} className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* Audit & Conseil */}
            <div className="mb-12">
              <AnimatedSection>
                <div className="card max-w-6xl mx-auto">
                  <div className="flex items-center mb-6">
                    <div className="p-3 rounded-full bg-blue-100">
                      <Brain className="text-blue-600 w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-semibold ml-4 text-gray-900">{auditConseil.title}</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-lg font-semibold mb-4 text-gray-900">Ce que nous faisons</h4>
                      <ul className="space-y-2">
                        {auditConseil.whatWeDo.map((item, i) => (
                          <li key={i} className="flex items-start text-gray-700">
                            <CheckCircle2 className="w-5 h-5 text-secondary mr-2 mt-0.5" />
                            <span className="text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-semibold mb-4 text-gray-900">Livrables</h4>
                      <div className="flex flex-wrap gap-2">
                        {auditConseil.deliverables.map((item, i) => (
                          <span key={i} className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>

            <div className="text-center">
              <Link 
                to="/contact"
                className="btn-primary inline-flex items-center"
              >
                Parler à un expert QA
                <MessageSquare className="ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* Business Analysis */}
        <section id="ba" className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Business Analysis</h2>
              <p className="subheading max-w-2xl mx-auto">
                Aligner le besoin métier, le produit et la qualité.
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {businessAnalysis.map(({ title, description, icon: Icon }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full hover:scale-105 transition-transform">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-full bg-secondary bg-opacity-10">
                        <Icon className="text-secondary w-8 h-8" />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <AnimatedSection>
              <div className="card bg-secondary bg-opacity-5 max-w-4xl mx-auto mb-8">
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Livrables</h3>
                <p className="text-gray-700">
                  Backlog priorisé, BRD/US, maquettes, plan de change, jeu de cas d'acceptation.
                </p>
              </div>
            </AnimatedSection>

            {/* Bouton expansion */}
            <div className="text-center">
              <button
                onClick={() => setShowApproach(!showApproach)}
                className="btn-primary inline-flex items-center"
              >
                {showApproach
                  ? "Fermer l’approche BA/PO"
                  : "Découvrir notre approche BA/PO"}
                <ArrowRight className="ml-2" />
              </button>
            </div>

            {/* Bloc expansion */}
            {showApproach && (
              <AnimatedSection>
                <div className="card bg-white shadow-lg max-w-4xl mx-auto p-6 mt-8">
                  <h3 className="text-2xl font-bold text-primary mb-4">
                    Notre approche BA/PO
                  </h3>
                  <ol className="space-y-4 text-left list-decimal list-inside text-gray-700">
                    <li>
                      <strong>Cadrage & vision produit</strong> : clarification du périmètre, business case, ateliers avec les parties prenantes.
                    </li>
                    <li>
                      <strong>Conception & prototypage</strong> : story mapping, maquettes, règles de gestion, validation avec IT & métiers.
                    </li>
                    <li>
                      <strong>Itération & gouvernance agile</strong> : backlog priorisé, revues régulières, ajustements basés sur feedback utilisateurs.
                    </li>
                    <li>
                      <strong>Accompagnement du changement</strong> : plan de communication, formation, suivi de l’adoption et KPIs.
                    </li>
                  </ol>
                  <p className="mt-6 text-gray-600">
                    ➜ Notre approche BA/PO garantit une{" "}
                    <span className="font-semibold">traçabilité de bout en bout</span>{" "}
                    et une{" "}
                    <span className="font-semibold">valeur livrée en continu</span>.
                  </p>
                </div>
              </AnimatedSection>
            )}
          </div>
        </section>

        {/* Gestion de projet */}
        <section id="project-management" className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Gestion de projet</h2>
              <p className="subheading max-w-2xl mx-auto">
                Des méthodologies au service de vos contraintes.
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {projectManagement.map(({ title, description, icon: Icon }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full hover:scale-105 transition-transform">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-full bg-primary bg-opacity-10">
                        <Icon className="text-primary w-8 h-8" />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <AnimatedSection>
              <div className="card bg-primary bg-opacity-5 max-w-4xl mx-auto">
                <h3 className="text-xl font-semibold mb-4 text-gray-900">Livrables</h3>
                <p className="text-gray-700">
                  Plans, indicateurs, registres risques, jalons, comités & CR.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Autres prestations */}
        <section id="other-services" className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Autres prestations</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {otherServices.map(({ title, description, deliverables, icon: Icon }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full hover:scale-105 transition-transform">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-full bg-secondary bg-opacity-10">
                        <Icon className="text-secondary w-8 h-8" />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700 mb-4">
                      {description.split('. ').map((sentence, i) => (
                        <span key={i} className="block mb-2">
                          {sentence.trim()}{i < description.split('. ').length - 1 ? '.' : ''}
                        </span>
                      ))}
                    </p>
                    {deliverables && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Livrables :</h4>
                        <p className="text-sm text-gray-600">{deliverables}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <a 
                href="https://qualis-academy.fr/formations" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center"
              >
                Explorer le catalogue de formations
                <BookOpen className="ml-2" />
              </a>
            </div>
          </div>
        </section>

        {/* Domaines d'intervention */}
<section id="domains" className="section bg-white">
  <div className="container">
    <AnimatedSection className="text-center mb-12">
      <h2 className="heading">Domaines d'intervention</h2>
      <p className="subheading max-w-2xl mx-auto">
        Adapter la qualité aux exigences de chaque métier.
      </p>
    </AnimatedSection>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
      {/* Banque & Assurance */}
      <motion.div
        custom={0}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={cardVariants}
      >
        <div className="card h-full hover:scale-105 transition-transform">
          <div className="flex items-center mb-4">
            <div className="p-3 rounded-full bg-primary bg-opacity-10">
              <Building2 className="text-primary w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold ml-4 text-gray-900">Banque & Assurance</h3>
          </div>
          <p className="text-gray-700">
            Parcours clients, KYC/paiement, conformité & sécurité, assurance santé et assurance vie.
          </p>
        </div>
      </motion.div>

      {/* Retraite & protection sociale */}
      <motion.div
        custom={1}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={cardVariants}
      >
        <div className="card h-full hover:scale-105 transition-transform">
          <div className="flex items-center mb-4">
            <div className="p-3 rounded-full bg-primary bg-opacity-10">
              <Clock className="text-primary w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold ml-4 text-gray-900">Retraite & Protection sociale</h3>
          </div>
          <p className="text-gray-700">
            Calculs complexes, référentiels, qualité des données, portails de gestion et services en ligne.
          </p>
        </div>
      </motion.div>

      {/* Télécoms & Industrie */}
      <motion.div
        custom={2}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={cardVariants}
      >
        <div className="card h-full hover:scale-105 transition-transform">
          <div className="flex items-center mb-4">
            <div className="p-3 rounded-full bg-primary bg-opacity-10">
              <Phone className="text-primary w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold ml-4 text-gray-900">Télécoms & Industrie</h3>
          </div>
          <p className="text-gray-700">
            Performance & scalabilité, applications selfcare, disponibilité et robustesse.
          </p>
        </div>
      </motion.div>

      {/* Hôtellerie & Services */}
      <motion.div
        custom={3}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={cardVariants}
      >
        <div className="card h-full hover:scale-105 transition-transform">
          <div className="flex items-center mb-4">
            <div className="p-3 rounded-full bg-primary bg-opacity-10">
              <Trophy className="text-primary w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold ml-4 text-gray-900">Hôtellerie & Services</h3>
          </div>
          <p className="text-gray-700">
            Expérience omnicanale, réservation en ligne, programmes de fidélité et digitalisation des parcours.
          </p>
        </div>
      </motion.div>

      {/* E-commerce & Distribution */}
      <motion.div
        custom={4}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={cardVariants}
      >
        <div className="card h-full hover:scale-105 transition-transform">
          <div className="flex items-center mb-4">
            <div className="p-3 rounded-full bg-primary bg-opacity-10">
              <ShoppingBag className="text-primary w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold ml-4 text-gray-900">E-commerce & Distribution</h3>
          </div>
          <p className="text-gray-700">
            Tunnels de vente fiables, performance web & mobile, optimisation UX et parcours client.
          </p>
        </div>
      </motion.div>

      {/* Comptabilité & ERP */}
      <motion.div
        custom={5}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={cardVariants}
      >
        <div className="card h-full hover:scale-105 transition-transform">
          <div className="flex items-center mb-4">
            <div className="p-3 rounded-full bg-primary bg-opacity-10">
              <Calculator className="text-primary w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold ml-4 text-gray-900">Comptabilité & ERP</h3>
          </div>
          <p className="text-gray-700">
            Solutions de gestion, comptabilité, automatisation des processus financiers et ERP.
          </p>
        </div>
      </motion.div>
    </div>

    {/* <div className="text-center">
      <Link 
        to="/blog"
        className="btn-primary inline-flex items-center"
      >
        Voir nos études de cas
        <Eye className="ml-2" />
      </Link>
    </div> */}
  </div>
</section>

        {/* Notre approche (méthodologie) */}
        <section id="methodology" className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Notre approche</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-8">
              {methodology.map(({ step, title, description, deliverables, icon: Icon }, i) => (
                <motion.div
                  key={step}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full hover:scale-105 transition-transform">
                    <div className="flex items-center mb-6">
                      <div className="relative">
                        <div className="p-3 rounded-full bg-primary bg-opacity-10">
                          <Icon className="text-primary w-8 h-8" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {step}
                        </div>
                      </div>
                      <div className="ml-4">
                        <span className="text-sm font-semibold text-secondary">Étape {step}</span>
                        <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4">{description}</p>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Livrables :</h4>
                      <p className="text-sm text-gray-600">{deliverables}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <Link 
                to="/audit"
                className="btn-primary inline-flex items-center"
              >
                Voir notre playbook QA
                <ArrowRight className="ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* Nos outils */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Nos outils</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {tools.map(({ category, tools }, i) => (
                <motion.div
                  key={category}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card text-center">
                    <h3 className="text-lg font-semibold mb-4 text-gray-900">{category}</h3>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {tools.map((tool) => (
                        <span 
                          key={tool}
                          className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full"
                        >
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <AnimatedSection>
              <div className="card bg-blue-50 max-w-4xl mx-auto text-center">
                <p className="text-gray-700">
                  <strong>Note :</strong> Nous travaillons en priorité avec vos outils existants, 
                  puis recommandons des évolutions si nécessaire.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* CTA final */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center">
              <div className="max-w-3xl mx-auto">
                <h2 className="heading mb-6">
                  Prêt à sécuriser vos mises en production et gagner en vélocité ?
                </h2>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to="/contact"
                    className="btn-primary inline-flex items-center"
                  >
                    Parler à un expert QA
                    <MessageSquare className="ml-2" />
                  </Link>
                  <Link 
                    to="/audit"
                    className="btn-secondary inline-flex items-center"
                  >
                    Demander un audit QA (offert – 30 min)
                    <ArrowRight className="ml-2" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Expertise;
